import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUserSettings } from '@/hooks/useUserSettings';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Button } from "@/components/ui/button";
import { Save, LogOut, Book } from "lucide-react";
import {
  UsageSection,
  FormTagSection,
  ImportanceSection,
  SubjectSection,
  PromptSection
} from './components';

const Dashboard: React.FC = () => {
  const { toast } = useToast();
  const { userEmail, signOut } = useAuth();
  const { userSettings, isLoading, error, updateSettings } = useUserSettings();
  const [isDirty, setIsDirty] = useState({
    importance: false,
    subject: false,
    prompt: false
  });
  const [isPro] = useState(true);
  const [settings, setSettings] = useState({
    importance: {
      high: { threshold: 80, label: '重要' },
      medium: { threshold: 50, label: '通常' },
      low: { label: '低' }
    },
    subjectTemplate: 'WEBサイトからのお問い合わせ',
    prompt: `お客様からの問い合わせはとても重要だと考えています。
また、購入を検討している方からの問い合わせも重要です。
営業メールの重要度は高くありません。`
  });

  useEffect(() => {
    if (userSettings) {
      setSettings({
        importance: userSettings.importance,
        subjectTemplate: userSettings.subjectTemplate || 'WEBサイトからのお問い合わせ',
        prompt: userSettings.prompt
      });
    }
  }, [userSettings]);

  const handleSave = async () => {
    try {
      const updateData: {
        importance: typeof settings.importance;
        prompt: string;
        subjectTemplate?: string;
      } = {
        importance: settings.importance,
        prompt: settings.prompt
      };

      if (settings.subjectTemplate) {
        updateData.subjectTemplate = settings.subjectTemplate;
      }

      console.log('保存するデータ:', JSON.stringify(updateData, null, 2));
      await updateSettings(updateData);
      setIsDirty({
        importance: false,
        subject: false,
        prompt: false
      });
      toast({
        title: "設定を保存しました",
        description: "変更内容が正常に保存されました",
        variant: "default"
      });
    } catch (err) {
      toast({
        title: "保存に失敗しました",
        description: err instanceof Error ? err.message : "エラーが発生しました",
        variant: "destructive"
      });
    }
  };

  const handleLabelChange = (key: 'high' | 'medium' | 'low', value: string) => {
    setSettings(prev => ({
      ...prev,
      importance: {
        ...prev.importance,
        [key]: {
          ...prev.importance[key],
          label: value
        }
      }
    }));
    setIsDirty(prev => ({
      ...prev,
      importance: true
    }));
  };

  const handlePromptChange = (value: string) => {
    setSettings(prev => ({
      ...prev,
      prompt: value
    }));
    setIsDirty(prev => ({
      ...prev,
      prompt: true
    }));
  };

  const handleSubjectTemplateChange = (value: string) => {
    setSettings(prev => ({
      ...prev,
      subjectTemplate: value
    }));
    setIsDirty(prev => ({
      ...prev,
      subject: true
    }));
  };

  const handleImportanceChange = ([medium, high]: [number, number]) => {
    setSettings(prev => ({
      ...prev,
      importance: {
        ...prev.importance,
        medium: { ...prev.importance.medium, threshold: medium },
        high: { ...prev.importance.high, threshold: high }
      }
    }));
    setIsDirty(prev => ({
      ...prev,
      importance: true
    }));
  };

  const navigate = useNavigate();

  if (isLoading) return <div>読み込み中...</div>;
  
  if (error) {
    // エラー発生時にログアウトして/appにリダイレクト
    signOut().then(() => {
      toast({
        title: "エラーが発生しました",
        description: "ユーザー情報の取得に失敗しました。再度ログインしてください。",
        variant: "destructive"
      });
      navigate('/app');
    });
    return null;
  }

  const labels = [
    { value: settings.importance.low.label, label: "低" },
    { value: settings.importance.medium.label, label: "中" },
    { value: settings.importance.high.label, label: "高" }
  ];

  const hasChanges = Object.values(isDirty).some(value => value);

  return (
    <div className="min-h-screen p-0 md:p-6">
      <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 md:py-4">
        <div className="flex justify-between items-center max-w-3xl mx-auto">
          <div className="text-sm text-gray-600 truncate max-w-[60%]">
            {userEmail}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              const success = await signOut();
              if (success) {
                toast({
                  title: "ログアウトしました"
                });
              } else {
                toast({
                  title: "ログアウトに失敗しました",
                  variant: "destructive"
                });
              }
            }}
            className="text-gray-600 hover:text-gray-900"
          >
            <LogOut className="w-4 h-4 mr-2" />
            ログアウト
          </Button>
        </div>
      </header>
      <div className="p-0 md:p-6 space-y-4 md:space-y-6 max-w-3xl mx-auto">
      <UsageSection
        usage={userSettings?.monthlyUsage || 0}
        limit={250}
      />

      <PromptSection
        prompt={settings.prompt}
        isDirty={isDirty.prompt}
        onChange={handlePromptChange}
      />

      <ImportanceSection
        values={[settings.importance.medium.threshold, settings.importance.high.threshold]}
        labels={labels}
        isPro={isPro}
        isDirty={isDirty.importance}
        onChange={handleImportanceChange}
      />

      <SubjectSection
        subjectTemplate={settings.subjectTemplate}
        importance={settings.importance}
        isDirty={isDirty.subject}
        onSubjectTemplateChange={handleSubjectTemplateChange}
        onLabelChange={handleLabelChange}
      />

        <Button 
          onClick={handleSave} 
          disabled={!hasChanges}
          className="w-full"
        >
          <Save className="w-4 h-4 mr-2" />
          すべての変更を保存
        </Button>

        <FormTagSection
          apiKey={userSettings?.apiKey || ''}
        />

        <div className="mt-4 text-center">
          <Link 
            to="/guidelines" 
            className="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Book className="w-4 h-4 mr-2" />
            フォーム作成ガイドラインを確認する →
          </Link>
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default Dashboard;
